import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSRMS } from '../../context/SRMSContext';
import { firebaseFirestoreService } from '../../services/firebaseFirestoreService';
import { Enquiry } from '../../types';
import { nowLocalStamp } from '../../utils/dates';
import logo from '../../assets/scalesupport-logo.png';
import handshake from '../../assets/handshake.webp';
import galFieldVisit from '../../assets/g-fieldvisit.webp';
import galReporting from '../../assets/g-reporting.webp';
import galBackOffice from '../../assets/g-backoffice.webp';
import galSrms from '../../assets/g-srms.webp';
import galTeam from '../../assets/g-team.webp';
import galReview from '../../assets/g-review.webp';

/**
 * THE PUBLIC WEBSITE AT scalesupport.co.in.
 *
 * Everything that is not /login is this page. It carries the company's own
 * story -- what ScaleSupport does, what SRMS is, who we are empanelled with
 * -- and two forms:
 *
 *   1. SIGN IN. The same credentials and the same call as the SRMS login
 *      screen, so there is one way in and one place where sign-in is
 *      decided. A visitor who signs in here lands straight in the system.
 *
 *   2. JOIN US. An agency or a bank asking to be empanelled. It is written
 *      to the `enquiries` collection, which is the only collection in the
 *      database that accepts a write from somebody who is not signed in --
 *      a create, in a fixed shape, with capped lengths, and no read. The
 *      administrator reads them in Settings.
 *
 * WHY THE STYLES ARE IN HERE AND SCOPED. The application is built on
 * Tailwind and this page is not; it has its own typography, its own
 * palette, and plain element rules (`h1`, `input`, `label`). Left global,
 * those rules would reach into every screen of SRMS. Every selector below
 * therefore sits under `.ss-site`, which wraps the whole page and exists
 * nowhere else.
 */

const CSS = `
.ss-site {
  --navy:      #0E2E63;
  --navy-mid:  #16418A;
  --navy-deep: #082044;
  --red:       #D81F26;
  --blue-wash: #EAF1FB;
  --cream:     #FDF4DE;
  --paper:     #FFFFFF;
  --mist:      #F4F7FC;
  --rule:      #DBE4F1;
  --ink:       #0E2E63;
  --ink-soft:  #475874;
  --ink-faint: #74849C;
  --on-navy:   #C2D2EA;
  --display: 'Archivo', 'Segoe UI', system-ui, sans-serif;
  --body: 'Source Sans 3', 'Segoe UI', system-ui, sans-serif;

  background: var(--paper);
  color: var(--ink);
  font-family: var(--body);
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}
.ss-site * { box-sizing: border-box; }
.ss-site h1, .ss-site h2, .ss-site h3, .ss-site h4 {
  font-family: var(--display); margin: 0; letter-spacing: -0.015em; text-wrap: balance;
}
.ss-site p { margin: 0; }
.ss-site img { display: block; max-width: 100%; }
.ss-site :focus-visible { outline: 3px solid #B8860B; outline-offset: 2px; }

.ss-site .wrap { max-width: 1180px; margin: 0 auto; padding-inline: 24px; }
.ss-site .sec { padding-block: 64px; }
.ss-site .sec-sm { padding-block: 44px; }
.ss-site .band-mist { background: var(--mist); border-block: 1px solid var(--rule); }
.ss-site .band-navy { background: var(--navy); color: #fff; }
.ss-site .band-navy p { color: var(--on-navy); }
.ss-site .eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--navy-mid); }
.ss-site .h2 { font-size: clamp(25px, 2.6vw, 31px); font-weight: 800; margin-top: 10px; }
.ss-site .sub { color: var(--ink-soft); margin-top: 12px; max-width: 620px; }

.ss-site .hdr {
  position: sticky; top: 0; z-index: 40; background: var(--paper);
  border-bottom: 1px solid var(--rule);
  transition: box-shadow .2s ease;
}
/* Once the reader has scrolled, the bar slims down: a 124px logo with the
   menu and the Login button wrapping underneath was taking roughly a third
   of a laptop screen on every section of the page. The logo is still large
   on arrival, which is the moment it matters. */
.ss-site .hdr.compact { box-shadow: 0 2px 10px rgba(14,46,99,.08); }
.ss-site .hdr.compact .logo img { height: clamp(42px, 4.2vw, 58px); }
.ss-site .hdr.compact .hdr-in { padding-block: 8px; }
.ss-site .hdr-in { display: flex; flex-wrap: wrap; align-items: center; gap: 18px; padding-block: 12px; transition: padding .2s ease; }
.ss-site .hdr-in > .logo { order: 1; }
.ss-site .hdr-in > .menu { order: 2; }
.ss-site .hdr-in > .btn { order: 3; }
.ss-site .logo { flex-shrink: 0; background: none; border: 0; padding: 0; cursor: pointer; }
.ss-site .logo img { height: clamp(68px, 8.4vw, 124px); width: auto; transition: height .2s ease; }
/* min-width:0 lets the menu shrink and wrap inside itself rather than
   shoving the Login button onto a row of its own. */
.ss-site .menu { display: flex; flex-wrap: wrap; align-items: center; gap: 22px; flex: 1 1 auto; min-width: 0; }
.ss-site .menu button {
  background: none; border: 0; padding: 10px 0; min-height: 44px; cursor: pointer;
  font-family: var(--display); font-size: 16px; font-weight: 600; color: var(--navy);
  border-bottom: 2px solid transparent;
}
.ss-site .menu button:hover { color: var(--navy-mid); }
.ss-site .menu button[aria-current="page"] { border-bottom-color: var(--red); }

.ss-site .btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font-family: var(--display); font-weight: 700; font-size: 15px;
  min-height: 44px; padding: 12px 22px; border-radius: 8px; border: 0; cursor: pointer;
}
.ss-site .btn-red { background: var(--red); color: #fff; }
.ss-site .btn-red:hover { background: #B4151C; }
.ss-site .btn-red:disabled { opacity: .6; cursor: default; }
.ss-site .btn-navy { background: var(--navy); color: #fff; }
.ss-site .btn-navy:hover { background: var(--navy-deep); }
.ss-site .btn-navy:disabled { opacity: .6; cursor: default; }
.ss-site .btn-block { width: 100%; }
.ss-site .btn-lg { font-size: 17px; min-height: 52px; }

.ss-site .hero {
  background:
    radial-gradient(900px 420px at 88% 8%, #DCE8F9 0%, rgba(220,232,249,0) 62%),
    linear-gradient(180deg, #F7FAFF 0%, #EEF4FD 100%);
  border-bottom: 1px solid var(--rule);
}
.ss-site .hero-in { display: flex; flex-wrap: wrap; gap: 40px; align-items: center; padding-block: 56px 62px; }
.ss-site .hero-copy { flex: 999 1 420px; min-width: 0; }
.ss-site .hero-side { flex: 1 1 360px; min-width: 0; }
.ss-site .h1 { font-size: clamp(32px, 4.2vw, 50px); font-weight: 800; line-height: 1.08; }
.ss-site .h1 em { font-style: normal; color: var(--red); }
.ss-site .hero-sub { font-size: 18px; color: var(--ink-soft); margin-top: 18px; max-width: 520px; }

.ss-site .marks { display: flex; flex-wrap: wrap; gap: 26px; margin-top: 22px; }
.ss-site .mark { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ss-site .mark span.t { font-family: var(--display); font-weight: 700; font-size: 15px; }
.ss-site .ico {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: var(--navy); color: #fff;
}
.ss-site .ico-sm { width: 22px; height: 22px; border-radius: 6px; }
.ss-site .ico-wash { background: var(--blue-wash); color: var(--navy); }
.ss-site .ico-cream { background: #F6E6BC; color: #8A6410; }
.ss-site .ico-ghost { background: rgba(255,255,255,.14); color: #fff; }

.ss-site .login { background: var(--paper); border: 1px solid var(--rule); border-radius: 14px; overflow: hidden; box-shadow: 0 18px 44px rgba(10,32,68,.14); }
.ss-site .login-head { background: var(--navy); color: #fff; padding: 20px 24px; text-align: center; }
.ss-site .login-head h2 { font-size: 21px; font-weight: 800; display: flex; align-items: center; justify-content: center; gap: 9px; }
.ss-site .login-head p { color: var(--on-navy); font-size: 13px; margin-top: 4px; }
.ss-site .login-body { padding: 22px 24px 24px; display: flex; flex-direction: column; gap: 12px; }
.ss-site .field { position: relative; }
.ss-site .field .fi { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--ink-faint); pointer-events: none; display: flex; }
.ss-site input[type="text"], .ss-site input[type="password"],
.ss-site input[type="tel"], .ss-site input[type="email"], .ss-site textarea {
  width: 100%; min-height: 48px; padding: 12px 14px; font-size: 16px; font-family: inherit;
  color: var(--ink); background: var(--paper); border: 1px solid #C7D4E6; border-radius: 8px;
}
.ss-site .field input { padding-left: 40px; }
.ss-site input::placeholder, .ss-site textarea::placeholder { color: #93A3B9; }
.ss-site input:focus, .ss-site textarea:focus { border-color: var(--navy-mid); outline: none; }
.ss-site label { display: block; font-size: 13px; font-weight: 700; color: var(--navy); margin-bottom: 6px; }
.ss-site .req { color: var(--red); }
.ss-site .login-foot { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; font-size: 13px; }
.ss-site .login-foot label { display: flex; align-items: center; gap: 7px; margin: 0; font-weight: 600; color: var(--ink-soft); }
.ss-site .login-foot input[type="checkbox"] { min-height: 0; width: auto; }
.ss-site .linkish { background: none; border: 0; padding: 0; font: inherit; font-weight: 700; color: var(--navy-mid); cursor: pointer; }
.ss-site .note { font-size: 13px; color: var(--ink-faint); }
.ss-site .note-err { font-size: 13px; color: #A8111B; font-weight: 700; }

.ss-site .grid { display: grid; gap: 22px; }
.ss-site .g2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.ss-site .g3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.ss-site .g4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.ss-site .g-14 { gap: 14px; }
.ss-site .g-16 { gap: 16px; }
.ss-site .g-40 { gap: 40px; }
.ss-site .ai-center { align-items: center; }
.ss-site .ai-start { align-items: start; }
.ss-site .mt-0 { margin-top: 0; }
.ss-site .mt-24 { margin-top: 24px; }
.ss-site .mt-28 { margin-top: 28px; }
.ss-site .mt-30 { margin-top: 30px; }

.ss-site .panel { border-radius: 14px; padding: 28px; min-width: 0; border: 1px solid var(--rule); }
.ss-site .panel-blue { background: var(--blue-wash); border-color: #CBDDF5; }
.ss-site .panel-cream { background: var(--cream); border-color: #EEDCAE; }
.ss-site .panel-mist { background: var(--mist); }
.ss-site .panel h3 { font-size: 21px; font-weight: 800; }
.ss-site .panel > p { color: var(--ink-soft); margin-top: 10px; }
.ss-site .list { display: flex; flex-direction: column; gap: 11px; margin-top: 18px; }
.ss-site .li { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 600; min-width: 0; }

.ss-site .card { background: var(--paper); border: 1px solid var(--rule); border-radius: 12px; padding: 24px; min-width: 0; }
.ss-site .card h3 { font-size: 17px; font-weight: 700; margin-top: 12px; }
.ss-site .card p { color: var(--ink-soft); font-size: 15px; margin-top: 8px; }

.ss-site .strip { display: flex; flex-wrap: wrap; gap: 26px; }
.ss-site .strip > div { flex: 1 1 200px; display: flex; align-items: center; gap: 12px; min-width: 0; }
.ss-site .strip h4 { font-size: 16px; font-weight: 700; color: #fff; line-height: 1.3; }

.ss-site .badge-soft {
  display: inline-block; background: #F6E6BC; color: #7A5A08; font-weight: 700;
  font-size: 12px; padding: 5px 12px; border-radius: 999px; margin-top: 10px;
}
.ss-site .trust { display: flex; flex-wrap: wrap; gap: 18px; }
.ss-site .trust > div { flex: 1 1 150px; text-align: center; min-width: 0; }
.ss-site .trust .ico { margin: 0 auto 8px; width: 42px; height: 42px; }
.ss-site .trust p { font-size: 13px; font-weight: 700; color: var(--ink-soft); }

/* The handshake, beside the empanelment form.
   A cut-out needs clear space: laid over the hero it was sliced by the
   sign-in card and the cuff ran straight through the headline. */
.ss-site .shake {
  margin-top: 26px; border-radius: 14px; overflow: hidden;
  background: linear-gradient(180deg, #EAF1FB 0%, #DCE8F9 100%);
  border: 1px solid #CBDDF5; padding: 18px 20px 0;
}
.ss-site .shake img { width: 100%; height: auto; display: block; }

/* The gallery. Each picture is its own file, so one can be replaced
   without touching the others. Fixed aspect ratios keep the rows even
   whatever size the photograph arrives at. */
.ss-site .gal { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; margin-top: 34px; }
.ss-site .gal figure { margin: 0; background: var(--paper); border: 1px solid var(--rule); border-radius: 12px; overflow: hidden; min-width: 0; }
.ss-site .gal .shot { aspect-ratio: 4 / 3; overflow: hidden; background: var(--blue-wash); }
.ss-site .gal .shot img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ss-site .gal .gal-wide { grid-column: span 2; }
.ss-site .gal .gal-wide .shot { aspect-ratio: 8 / 3; }
.ss-site .gal .gal-wide .shot img { object-fit: contain; background: #0B1F42; }
.ss-site .gal .gal-full { grid-column: span 3; }
.ss-site .gal .gal-full .shot { aspect-ratio: 12 / 3.4; }
.ss-site .gal figcaption { padding: 15px 18px 17px; }
.ss-site .gal figcaption .t { font-family: var(--display); font-size: 16px; font-weight: 700; }
.ss-site .gal figcaption .dsc { color: var(--ink-soft); font-size: 14px; margin-top: 5px; }

.ss-site .tagline {
  font-family: var(--display); font-size: 17px; font-weight: 700; color: var(--navy);
  margin-top: 20px; padding-left: 14px; border-left: 3px solid var(--red);
}

.ss-site .contact-card { background: var(--paper); border: 1px solid var(--rule); border-radius: 12px; padding: 22px; min-width: 0; }
.ss-site .contact-card .k { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-faint); }
.ss-site .contact-card .v { font-family: var(--display); font-size: 18px; font-weight: 700; margin-top: 7px; word-break: break-word; }

.ss-site .ok { background: #E8F6EE; border: 1px solid #A6D8BC; border-radius: 8px; padding: 14px; font-size: 14px; color: #13603A; font-weight: 600; margin-top: 14px; }
.ss-site .bad { background: #FBE9EA; border: 1px solid #E7B2B7; border-radius: 8px; padding: 12px 14px; font-size: 14px; color: #8C0D18; font-weight: 600; margin-top: 12px; }

.ss-site footer { background: var(--navy-deep); color: #fff; padding-block: 42px 22px; }
.ss-site footer p { color: #8FA6C6; }
.ss-site .foot-top { display: flex; flex-wrap: wrap; gap: 32px; justify-content: space-between; align-items: flex-start; }
.ss-site .foot-logo { background: #fff; display: inline-block; padding: 12px 16px; border-radius: 10px; }
.ss-site .foot-logo img { height: clamp(72px, 8vw, 118px); width: auto; }
.ss-site .foot-col { flex: 0 1 180px; min-width: 0; }
.ss-site .foot-col .k { font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #7B93B6; }
.ss-site .foot-col div { display: flex; flex-direction: column; gap: 8px; margin-top: 11px; }
.ss-site .foot-col button { color: #CFDEF2; font-size: 15px; background: none; border: 0; padding: 0; text-align: left; font-family: inherit; cursor: pointer; }
.ss-site .foot-col button:hover { color: #fff; }
.ss-site .foot-tag { font-family: var(--display); font-size: 15px; font-weight: 700; color: var(--on-navy); margin-top: 14px; }
.ss-site .foot-base { border-top: 1px solid rgba(255,255,255,.14); margin-top: 30px; padding-top: 16px; display: flex; flex-wrap: wrap; gap: 10px; justify-content: space-between; }
.ss-site .foot-base p { font-size: 13px; }

@media (max-width: 980px) {
  .ss-site .g4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ss-site .hero-in { gap: 32px; padding-block: 44px 50px; }
  .ss-site .gal { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ss-site .gal .gal-wide, .ss-site .gal .gal-full { grid-column: span 2; }
  .ss-site .gal .gal-full .shot { aspect-ratio: 16 / 7; }
}
@media (max-width: 820px) {
  .ss-site .hdr-in { gap: 10px 14px; }
  .ss-site .hdr-in > .menu { order: 3; flex-basis: 100%; }
  .ss-site .hdr-in > .btn { order: 2; margin-left: auto; }
  .ss-site .menu { gap: 18px; overflow-x: auto; flex-wrap: nowrap; padding-bottom: 2px; scrollbar-width: none; }
  .ss-site .menu::-webkit-scrollbar { display: none; }
  .ss-site .menu button { white-space: nowrap; font-size: 15px; }
  .ss-site .hero-side { flex-basis: 100%; }
  .ss-site .login { max-width: 460px; }
}
@media (max-width: 640px) {
  .ss-site .g2, .ss-site .g3 { grid-template-columns: minmax(0, 1fr); }
  .ss-site .sec { padding-block: 44px; }
  .ss-site .sec-sm { padding-block: 34px; }
  .ss-site .wrap { padding-inline: 16px; }
  .ss-site .panel { padding: 20px; }
  .ss-site .card { padding: 20px; }
  .ss-site .btn { padding: 11px 16px; font-size: 14px; }
  .ss-site .marks { gap: 14px 20px; }
  .ss-site .trust > div { flex-basis: 44%; }
  .ss-site .login { max-width: none; }
  .ss-site .strip { gap: 18px; }
  .ss-site .strip > div { flex-basis: 100%; }
  .ss-site .gal { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  .ss-site .gal .gal-wide, .ss-site .gal .gal-full { grid-column: auto; }
  .ss-site .gal .gal-wide .shot, .ss-site .gal .gal-full .shot { aspect-ratio: 4 / 3; }
  .ss-site .gal .gal-wide .shot img { object-fit: cover; }
}
@media (max-width: 400px) {
  .ss-site .hero-in { padding-block: 34px 40px; }
  .ss-site .mark span.t { font-size: 14px; }
}
@media (prefers-reduced-motion: reduce) {
  .ss-site * { animation: none !important; transition: none !important; }
}
`;

/* ------------------------------------------------------------------ *
 * Small inline marks. Drawn here rather than pulled from an icon pack,
 * because the page uses six of them and a pack would be a download.
 * ------------------------------------------------------------------ */

const S = (d: React.SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...d,
});

const IconLock = ({ size = 15 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.4 })}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconUser = ({ size = 16 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.2 })}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </svg>
);
const IconShield = ({ size = 15 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.4 })}>
    <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z" />
  </svg>
);
const IconGear = ({ size = 15 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.4 })}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
  </svg>
);
const IconBars = ({ size = 15 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.4 })}>
    <path d="M5 20V10M12 20V4M19 20v-7" />
  </svg>
);
const IconTick = ({ size = 12 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 3 })}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconDoc = ({ size = 18 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.2 })}>
    <path d="M6 3h8l4 4v14H6z" />
    <path d="M14 3v4h4" />
  </svg>
);
const IconClock = ({ size = 18 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.2 })}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const IconTeam = ({ size = 18 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.2 })}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 19a6 6 0 0 1 12 0" />
    <path d="M16 11a3 3 0 1 0-1.5-5.6" />
  </svg>
);
const IconScales = ({ size = 18 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.2 })}>
    <path d="M12 3v18M5 7h14M7 7l-3 6a3.2 3.2 0 0 0 6 0zM17 7l-3 6a3.2 3.2 0 0 0 6 0z" />
  </svg>
);
const IconTrend = ({ size = 16 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.3 })}>
    <path d="M4 18l5-6 4 3 6-8" />
    <path d="M19 7h-4M19 7v4" />
  </svg>
);
const IconShieldTick = ({ size = 16 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.3 })}>
    <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const IconTarget = ({ size = 16 }: { size?: number }) => (
  <svg {...S({ width: size, height: size, strokeWidth: 2.3 })}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.2" />
  </svg>
);

/** The eight service cards, each with its own mark. */
const SERVICES: Array<{ title: string; body: string; icon: React.ReactNode }> = [
  {
    title: 'NPA account recovery',
    body: 'Sub-standard, doubtful and loss accounts worked with a visit record against each one.',
    icon: <IconDoc size={16} />,
  },
  {
    title: 'Field recovery operations',
    body: 'Geo-tagged, time-stamped visits with a written outcome on the account.',
    icon: (
      <svg {...S({ width: 16, height: 16, strokeWidth: 2.3 })}>
        <path d="M12 21s-7-5-7-10a7 7 0 0 1 14 0c0 5-7 10-7 10z" />
        <circle cx="12" cy="11" r="2.4" />
      </svg>
    ),
  },
  {
    title: 'Customer follow-up',
    body: 'Contact recorded against the borrower, not kept in somebody’s notebook.',
    icon: (
      <svg {...S({ width: 16, height: 16, strokeWidth: 2.3 })}>
        <path d="M5 5h14v12H8l-3 3z" />
      </svg>
    ),
  },
  {
    title: 'PTP monitoring',
    body: 'Promises with a date and an amount, chased before they expire.',
    icon: (
      <svg {...S({ width: 16, height: 16, strokeWidth: 2.3 })}>
        <rect x="4" y="5" width="16" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M4 10h16" />
      </svg>
    ),
  },
  {
    title: 'Collection support',
    body: 'Receipts recorded against the account; the balance follows the receipts.',
    icon: (
      <svg {...S({ width: 16, height: 16, strokeWidth: 2.3 })}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 10h5M9.5 14h5" />
      </svg>
    ),
  },
  {
    title: 'Recovery reporting',
    body: 'Branch-wise and zone-wise MIS the bank can tally against its own books.',
    icon: <IconBars size={16} />,
  },
  {
    title: 'Field activity monitoring',
    body: 'Who went where, when, and what came of it.',
    icon: <IconUser size={16} />,
  },
  {
    title: 'Technology-enabled operations',
    body: 'One record per account, open to the bank and to the agent alike.',
    icon: (
      <svg {...S({ width: 16, height: 16, strokeWidth: 2.3 })}>
        <rect x="3" y="5" width="18" height="12" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
];

const SRMS_FEATURES = [
  'Account management',
  'Recovery entry & collection',
  'Follow-up & PTP',
  'Agent activity tracking',
  'Reports & commission',
  'Dashboard & analytics',
];

const AGENCY_FEATURES = [
  'Loan recovery services',
  'Field recovery support',
  'Compliance-based recovery',
  'Experienced recovery team',
  'End-to-end recovery management',
];

const SECTION_IDS = ['home', 'about', 'srms', 'services', 'empanelment', 'gallery', 'contact'];

export const PublicSite: React.FC = () => {
  const { loginWithCredentials } = useSRMS();

  // ---- sign in ----
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // ---- join us ----
  const [agency, setAgency] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [joinError, setJoinError] = useState('');

  const [current, setCurrent] = useState('home');
  /** True once the page has been scrolled, which slims the header. */
  const [compact, setCompact] = useState(false);
  const loginRef = useRef<HTMLDivElement | null>(null);

  // Which section the reader is in, for the underline in the menu.
  useEffect(() => {
    const onScroll = () => {
      let active = 'home';
      SECTION_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) active = id;
      });
      setCurrent(active);
      setCompact(window.scrollY > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id: string) => {
    const el = id === 'login' ? loginRef.current : document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: id === 'login' ? 'center' : 'start' });
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = loginId.trim();
    const pwd = password;
    if (!id || !pwd) {
      setLoginError('Enter your login ID and password.');
      return;
    }
    setSigningIn(true);
    setLoginError('');
    try {
      const res = await loginWithCredentials(id, pwd, 'web');
      if (!res?.success) {
        setLoginError(res?.error || 'Those details were not accepted.');
        return;
      }

      /**
       * PUT /login IN THE ADDRESS BAR.
       *
       * The application swaps itself for the portal on its own -- there is
       * no navigation involved -- so without this the address would stay at
       * scalesupport.co.in while SRMS is on screen. That reads wrongly: the
       * address should say where you are.
       *
       * `pushState` changes the address without reloading, so the session
       * that has just been established is not thrown away. Nothing reads the
       * path again until the next sign-out, and anybody already signed in
       * gets the portal whatever the address, so this is appearance rather
       * than routing.
       */
      try {
        if (window.location.pathname.replace(/\/+$/, '').toLowerCase() !== '/login') {
          window.history.pushState({}, '', '/login');
        }
      } catch {
        /* An address bar that will not change is not a reason to fail a
           sign-in that has already succeeded. */
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Sign-in could not be completed.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = agency.trim();
    const mobile = phone.trim();
    if (!name) {
      setJoinError('Enter your agency name.');
      return;
    }
    if (!mobile) {
      setJoinError('Enter a contact number.');
      return;
    }
    setSending(true);
    setJoinError('');

    const enquiry: Enquiry = {
      id: `ENQ-${Date.now()}-${Math.floor(Math.random() * 9000 + 1000)}`,
      agencyName: name.slice(0, 120),
      contactPerson: '',
      mobile: mobile.slice(0, 20),
      email: email.trim().slice(0, 160),
      city: area.trim().slice(0, 120),
      message: '',
      // Local clock, not UTC: the admin panel prints this straight back.
      createdAt: nowLocalStamp(),
      status: 'new',
      source: 'website',
    };

    try {
      await firebaseFirestoreService.saveEnquiry(enquiry);
      setSent(true);
    } catch (err: any) {
      console.warn('Enquiry save notice:', err?.message || err);
      setJoinError(
        'That could not be sent just now. Please call 9657922770 or email ScaleSupport9@gmail.com.'
      );
    } finally {
      setSending(false);
    }
  };

  const menu = useMemo(
    () => [
      { id: 'home', label: 'Home' },
      { id: 'about', label: 'About Us' },
      { id: 'srms', label: 'Our Products' },
      { id: 'services', label: 'Services' },
      { id: 'empanelment', label: 'Empanelment' },
      { id: 'gallery', label: 'Gallery' },
      { id: 'contact', label: 'Contact Us' },
    ],
    []
  );

  return (
    <div className="ss-site">
      <style>{CSS}</style>

      <header className={compact ? 'hdr compact' : 'hdr'}>
        <div className="wrap hdr-in">
          <button className="logo" type="button" onClick={() => go('home')} aria-label="ScaleSupport home">
            <img src={logo} alt="ScaleSupport" />
          </button>
          <nav className="menu">
            {menu.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => go(m.id)}
                aria-current={current === m.id ? 'page' : undefined}
              >
                {m.label}
              </button>
            ))}
          </nav>
          <button className="btn btn-red" type="button" onClick={() => go('login')}>
            <IconLock />
            Login to SRMS
          </button>
        </div>
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="hero" id="home">
        <div className="wrap hero-in">
          <div className="hero-copy">
            <h1 className="h1">
              Recovery &amp; Business Support Solutions
              <br />
              for a <em>Stronger Tomorrow</em>
            </h1>
            <p className="hero-sub">
              Empowering financial institutions with efficient recovery and business support
              services.
            </p>
            <div className="marks">
              <div className="mark">
                <span className="ico"><IconShield /></span>
                <span className="t">Trusted Partner</span>
              </div>
              <div className="mark">
                <span className="ico"><IconGear /></span>
                <span className="t">Technology Driven</span>
              </div>
              <div className="mark">
                <span className="ico"><IconBars /></span>
                <span className="t">Result Oriented</span>
              </div>
            </div>
          </div>

          <div className="hero-side" ref={loginRef}>
            <div className="login">
              <div className="login-head">
                <h2>
                  <IconLock size={18} />
                  Login to SRMS
                </h2>
                <p>Access your Recovery Management System</p>
              </div>
              <form className="login-body" onSubmit={handleSignIn}>
                <div className="field">
                  <span className="fi"><IconUser /></span>
                  <input
                    id="ss-login-id"
                    type="text"
                    placeholder="User ID / Email"
                    autoComplete="username"
                    autoCapitalize="characters"
                    spellCheck={false}
                    aria-label="User ID or email"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                  />
                </div>
                <div className="field">
                  <span className="fi"><IconLock size={16} /></span>
                  <input
                    id="ss-login-pwd"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    aria-label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <button className="btn btn-red btn-lg btn-block" type="submit" disabled={signingIn}>
                  {signingIn ? 'Signing in…' : 'Login →'}
                </button>
                <div className="login-foot">
                  <label htmlFor="ss-remember">
                    <input id="ss-remember" type="checkbox" />
                    Remember me
                  </label>
                  {/* Password recovery lives on the SRMS login screen, which
                      has the whole reset flow. No second copy of it here. */}
                  <a className="linkish" href="/login" style={{ textDecoration: 'none' }}>
                    Forgot password?
                  </a>
                </div>
                {loginError ? (
                  <p className="note-err">{loginError}</p>
                ) : (
                  <p className="note">
                    Staff access only — recovery agents, branch managers and coordinators.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- what we offer ---------------- */}
      <section className="sec" id="srms">
        <div className="wrap">
          <p className="eyebrow">What we offer</p>
          <h2 className="h2">Two sides of one business</h2>
          <p className="sub">A field recovery agency, and the system that runs it.</p>

          <div className="grid g2 mt-30">
            <div className="panel panel-blue">
              <h3>ScaleSupport Recovery Management System</h3>
              <p>A digital platform for recovery agents to manage recovery activities efficiently.</p>
              <div className="list">
                {SRMS_FEATURES.map((f) => (
                  <div className="li" key={f}>
                    <span className="ico ico-sm ico-wash"><IconTick /></span>
                    {f}
                  </div>
                ))}
              </div>
            </div>

            <div className="panel panel-cream">
              <h3>Recovery Services Agency</h3>
              <span className="badge-soft">Empanelled with nationalised banks</span>
              <p>
                Professional recovery support for nationalised banks, with compliance and best
                practice.
              </p>
              <div className="list">
                {AGENCY_FEATURES.map((f) => (
                  <div className="li" key={f}>
                    <span className="ico ico-sm ico-cream"><IconTick /></span>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- empanelment ---------------- */}
      <section className="sec band-mist" id="empanelment">
        <div className="wrap">
          <div className="grid g2 g-40 ai-center">
            <div>
              <p className="eyebrow">Empanelment</p>
              <h2 className="h2">Empanelled with nationalised banks</h2>
              <p className="sub">
                We are empanelled as a recovery agency with nationalised banks, providing
                professional and compliant recovery support.
              </p>
            </div>
            <div className="trust">
              <div>
                <span className="ico ico-wash"><IconDoc /></span>
                <p>Banking compliance</p>
              </div>
              <div>
                <span className="ico ico-cream"><IconClock /></span>
                <p>RBI guidelines</p>
              </div>
              <div>
                <span className="ico ico-wash"><IconTeam /></span>
                <p>Professional team</p>
              </div>
              <div>
                <span className="ico ico-wash"><IconScales /></span>
                <p>Ethical practices</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- why ---------------- */}
      <section className="band-navy sec-sm">
        <div className="wrap strip">
          <div>
            <span className="ico ico-ghost"><IconTeam size={16} /></span>
            <h4>Efficient recovery support</h4>
          </div>
          <div>
            <span className="ico ico-ghost"><IconTrend /></span>
            <h4>Technology-enabled operations</h4>
          </div>
          <div>
            <span className="ico ico-ghost"><IconShieldTick /></span>
            <h4>Compliant &amp; ethical processes</h4>
          </div>
          <div>
            <span className="ico ico-ghost"><IconTarget /></span>
            <h4>Better results for banks</h4>
          </div>
        </div>
      </section>

      {/* ---------------- services ---------------- */}
      <section className="sec" id="services">
        <div className="wrap">
          <p className="eyebrow">Services</p>
          <h2 className="h2">What we do</h2>
          <p className="sub">Allocation to realisation, worked branch by branch.</p>

          <div className="grid g4 mt-28">
            {SERVICES.map((s) => (
              <div className="card" key={s.title}>
                <span className="ico ico-wash">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- about ---------------- */}
      <section className="sec band-mist" id="about">
        <div className="wrap">
          <div className="grid g2 g-40 ai-start">
            <div>
              <p className="eyebrow">About us</p>
              <h2 className="h2">Our commitment</h2>
              <p className="sub">
                To deliver technology-enabled, compliant and result-oriented recovery and business
                support solutions for financial institutions, while creating sustainable
                opportunities for our partners and teams.
              </p>
              <p className="tagline">Your Growth, Powered by Our Support.</p>
            </div>
            <div className="grid g-16">
              <div className="card">
                <h3 className="mt-0">Our vision</h3>
                <p>
                  To become a trusted and technology-driven organisation providing professional
                  recovery and recovery-management solutions.
                </p>
              </div>
              <div className="card">
                <h3 className="mt-0">Our mission</h3>
                <p>
                  To provide professional field recovery services supported by technology, helping
                  organisations manage recovery operations efficiently and transparently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- gallery ---------------- */}
      <section className="sec" id="gallery">
        <div className="wrap">
          <p className="eyebrow">Gallery</p>
          <h2 className="h2">Our work, on the ground</h2>
          <p className="sub">
            Field visits, branch coordination and recovery work across Maharashtra — carried out by
            our empanelled agents under bank instructions.
          </p>

          <div className="gal">
            <figure>
              <div className="shot">
                <img src={galFieldVisit} alt="An agent meeting a borrower at a shop" />
              </div>
              <figcaption>
                <div className="t">Field visit</div>
                <div className="dsc">An agent meeting a borrower at their place of business.</div>
              </figcaption>
            </figure>

            <figure>
              <div className="shot">
                <img src={galReporting} alt="An agent recording a visit on a mobile phone" />
              </div>
              <figcaption>
                <div className="t">Live reporting</div>
                <div className="dsc">
                  Visits, PTPs and recoveries logged from the field, the same day.
                </div>
              </figcaption>
            </figure>

            <figure>
              <div className="shot">
                <img src={galBackOffice} alt="The back office team working on recovery reports" />
              </div>
              <figcaption>
                <div className="t">Back office</div>
                <div className="dsc">Allocation, monitoring and reporting on SRMS.</div>
              </figcaption>
            </figure>

            <figure className="gal-wide">
              <div className="shot">
                <img src={galSrms} alt="The SRMS Agent Command Desk screen" />
              </div>
              <figcaption>
                <div className="t">SRMS — Agent Command Desk</div>
                <div className="dsc">
                  Every allocated account, its overdue position and the next action, on one screen.
                </div>
              </figcaption>
            </figure>

            <figure>
              <div className="shot">
                <img src={galTeam} alt="The ScaleSupport recovery team" />
              </div>
              <figcaption>
                <div className="t">Our team</div>
                <div className="dsc">Trained agents, verified and empanelled.</div>
              </figcaption>
            </figure>

            <figure className="gal-full">
              <div className="shot">
                <img src={galReview} alt="A portfolio review meeting" />
              </div>
              <figcaption>
                <div className="t">Case review</div>
                <div className="dsc">Portfolio review before the field round.</div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ---------------- join us ---------------- */}
      <section className="sec" id="join">
        <div className="wrap">
          <div className="grid g2 g-40 ai-start">
            <div>
              <p className="eyebrow">Work with us</p>
              <h2 className="h2">Join Us</h2>
              <p className="sub">
                We empanel recovery agencies and work with banks that need field capacity on
                stressed portfolios. Send your details and our team will contact you.
              </p>
              <div className="shake">
                <img src={handshake} alt="A handshake between two people in business dress" />
              </div>
            </div>
            <div className="panel panel-mist">
              {sent ? (
                <div className="ok">
                  Thank you for your interest in ScaleSupport. Our team will contact you soon.
                </div>
              ) : (
                <form className="grid g-14" onSubmit={handleJoin}>
                  <div>
                    <label htmlFor="ss-j-agency">
                      Agency Name <span className="req">*</span>
                    </label>
                    <input
                      id="ss-j-agency"
                      type="text"
                      placeholder="Enter agency name"
                      maxLength={120}
                      value={agency}
                      onChange={(e) => setAgency(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="ss-j-phone">
                      Contact Number <span className="req">*</span>
                    </label>
                    <input
                      id="ss-j-phone"
                      type="tel"
                      inputMode="tel"
                      placeholder="10-digit mobile number"
                      maxLength={20}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="grid g2 g-14">
                    <div>
                      <label htmlFor="ss-j-area">Area</label>
                      <input
                        id="ss-j-area"
                        type="text"
                        placeholder="Optional"
                        maxLength={120}
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                      />
                    </div>
                    <div>
                      <label htmlFor="ss-j-email">Email ID</label>
                      <input
                        id="ss-j-email"
                        type="email"
                        placeholder="Optional"
                        maxLength={160}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <button className="btn btn-navy btn-block" type="submit" disabled={sending}>
                    {sending ? 'Sending…' : 'SUBMIT'}
                  </button>
                  {joinError && <div className="bad">{joinError}</div>}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- contact ---------------- */}
      <section className="sec band-mist" id="contact">
        <div className="wrap">
          <p className="eyebrow">Contact us</p>
          <h2 className="h2">Get in touch</h2>
          <div className="grid g3 mt-24">
            <div className="contact-card">
              <div className="k">Mobile</div>
              <div className="v">9657922770</div>
            </div>
            <div className="contact-card">
              <div className="k">Email</div>
              <div className="v">ScaleSupport9@gmail.com</div>
            </div>
            <div className="contact-card">
              <div className="k">Website</div>
              <div className="v">scalesupport.co.in</div>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="foot-top">
            <div>
              <span className="foot-logo">
                <img src={logo} alt="ScaleSupport" />
              </span>
              <p className="foot-tag">Your Growth, Powered by Our Support.</p>
            </div>
            <div className="foot-col">
              <div className="k">Company</div>
              <div>
                <button type="button" onClick={() => go('about')}>About Us</button>
                <button type="button" onClick={() => go('services')}>Services</button>
                <button type="button" onClick={() => go('gallery')}>Gallery</button>
                <button type="button" onClick={() => go('contact')}>Contact Us</button>
              </div>
            </div>
            <div className="foot-col">
              <div className="k">Recovery</div>
              <div>
                <button type="button" onClick={() => go('srms')}>Our Products</button>
                <button type="button" onClick={() => go('empanelment')}>Empanelment</button>
                <button type="button" onClick={() => go('login')}>Login to SRMS</button>
              </div>
            </div>
          </div>
          <div className="foot-base">
            <p>© {new Date().getFullYear()} ScaleSupport. All rights reserved.</p>
            <p>scalesupport.co.in</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicSite;
